import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

import { API } from '../config/api';

// Configure axios defaults
axios.defaults.withCredentials = true;

// Auto-refresh session: on 401, try /auth/refresh once then retry the original request.
let refreshPromise = null;
axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const url = original?.url || '';
    const isAuthCall = ['/auth/login', '/auth/refresh', '/auth/register', '/auth/logout', '/auth/me'].some((p) => url.includes(p));
    if (error.response?.status === 401 && original && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        refreshPromise = refreshPromise || axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        await refreshPromise;
        refreshPromise = null;
        return axios(original);
      } catch (e) {
        refreshPromise = null;
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur s'est produite. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatApiError(error) {
  // Network error (no response) - typical Safari ITP / CORS / offline scenario
  if (error && !error.response) {
    return "Connexion au serveur impossible. Veuillez vérifier votre connexion ou réessayer dans un instant.";
  }
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (status === 401) {
    return "Email ou mot de passe incorrect. Vérifiez vos identifiants.";
  }
  if (status === 429) {
    return "Trop de tentatives échouées. Veuillez patienter 15 minutes avant de réessayer.";
  }
  if (status >= 500) {
    return "Service momentanément indisponible. Réessayez dans quelques instants.";
  }
  return formatApiErrorDetail(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: formatApiError(error) };
    }
  };

  const register = async (name, email, password, phone, chosen_plan, signup_source, extras = {}) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { name, email, password, phone, chosen_plan, signup_source, ...extras }, { withCredentials: true });
      setUser(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: formatApiError(error) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(false);
    }
  };

  const refreshToken = async () => {
    try {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      await checkAuth();
      return true;
    } catch (error) {
      setUser(false);
      return false;
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      return response.data;
    } catch (error) {
      return null;
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshToken,
    refreshUser,
    isAuthenticated: !!user && user !== false,
    isCoach: user && (user.role === 'coach' || user.role === 'admin'),
    isAdmin: user && user.role === 'admin',
    isCoachRole: user && user.role === 'coach',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
